import { createContext, useState, useContext, useMemo } from 'react';

import type { GetSellerWhitelabellingResponse } from '@iwoca/lapi-client/iwocapay';

import { useQueryParam } from '../../hooks/useQueryParam';

export type WhiteLabelTheme = GetSellerWhitelabellingResponse['data'];

/**
 * When set to `true`, the seller's saved theme is applied even though the
 * white_labelled pilot is off — lets Account Managers preview unpublished
 * branding via a real pay-link before going live. Read once here so the whole
 * SPA journey (which sits under this provider) honours it. Imported by
 * Buyer/utils/queryParams.ts to whitelist it so it survives in-journey nav.
 */
export const PREVIEW_WHITELABEL_PARAM = 'preview_whitelabel';

type WhiteLabelProviderValue = {
  theme: WhiteLabelTheme;
  setTheme: React.Dispatch<React.SetStateAction<WhiteLabelTheme>>;
  // derived values
  showSellerLogo: boolean;
  isTheme: boolean;
  isThemeResolved: boolean;
  setIsThemeResolved: React.Dispatch<React.SetStateAction<boolean>>;
  isWhiteLabelPreview: boolean;
};

const INITIAL_THEME: WhiteLabelTheme = {
  logo_uri: null,
  preferred_logo_type: null,
  primary_color: null,
  secondary_color: null,
  text_color: null,
};

const WhiteLabelContext = createContext<WhiteLabelProviderValue | undefined>(
  undefined,
);

/**
 * N.B. Must be mounted inside a Router — it reads the preview query param via
 * useQueryParam (useLocation), which throws outside a Router context. In
 * Storybook, list this decorator before `router` (the last decorator is the
 * outermost wrapper).
 */
export const WhiteLabelProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [theme, setTheme] = useState<WhiteLabelTheme>(INITIAL_THEME);
  const [isThemeResolved, setIsThemeResolved] = useState(false);
  const isWhiteLabelPreview =
    useQueryParam(PREVIEW_WHITELABEL_PARAM) === 'true';

  const showSellerLogo = useMemo(
    () => !!theme.logo_uri && !!theme.preferred_logo_type,
    [theme.logo_uri, theme.preferred_logo_type],
  );

  const isTheme = useMemo(
    () =>
      !!theme.primary_color || !!theme.secondary_color || !!theme.text_color,
    [theme.primary_color, theme.secondary_color, theme.text_color],
  );

  // Memoised so consumers don't re-render on every navigation: reading the
  // preview flag subscribes this provider to location, so the value object
  // would otherwise be recreated on each route change.
  const value = useMemo(
    () => ({
      theme,
      setTheme,
      showSellerLogo,
      isTheme,
      isThemeResolved,
      setIsThemeResolved,
      isWhiteLabelPreview,
    }),
    [theme, showSellerLogo, isTheme, isThemeResolved, isWhiteLabelPreview],
  );

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

export function useWhiteLabelTheme() {
  const ctx = useContext(WhiteLabelContext);
  if (!ctx)
    throw new Error(
      'useWhiteLabelTheme must be used within a WhiteLabelProvider',
    );
  return ctx;
}
